import { useCombobox, UseComboboxGetItemPropsOptions } from "downshift";
import { Dispatch, SetStateAction, useState } from "react";
import {
  MainMenu,
  ClearButton,
  ToggleButton,
  ArrowDown,
  ArrowUp,
  ClearButtonSymbol,
  DropdownContainer,
  DropdownInputField,
  ComboBoxContainer,
  SrOnlyLabel,
} from "./styles";

interface SearchBoxItem {
  id: string;
  label: string;
  productId?: string;
}

interface SearchItemRenderProps {
  item: SearchBoxItem;
  index: number;
  highlightedIndex: number;
  selectedItem: SearchBoxItem | null;
  mappedItemIndex: number;
  getItemProps: (
    options: UseComboboxGetItemPropsOptions<SearchBoxItem>,
  ) => Record<string, unknown>;
  hashFunction: (item: SearchBoxItem) => string;
  displayItems: SearchBoxItem[];
  setDisplayItems: Dispatch<SetStateAction<SearchBoxItem[]>>;
}

interface ComboBoxInput {
  items: SearchBoxItem[];
  hashFunction: (item: SearchBoxItem) => string;
  placeholder: string;
  ItemRenderComponent: React.ComponentType<SearchItemRenderProps>;
  onChangeEvent: (args: { id: string | undefined }) => void;
  selectedValue: SearchBoxItem | undefined;
}

const SearchBoxPrimitive = ({
  items,
  hashFunction,
  placeholder,
  ItemRenderComponent,
  onChangeEvent,
  selectedValue,
}: ComboBoxInput) => {
  function ComboBox() {
    const [displayItems, setDisplayItems] = useState(items);

    const {
      getToggleButtonProps,
      getMenuProps,
      getInputProps,
      getItemProps,
      getLabelProps,
      selectedItem,
      highlightedIndex,
      isOpen,
      selectItem,
      setInputValue,
      inputValue,
    } = useCombobox({
      onInputValueChange({ inputValue, isOpen }) {
        if (inputValue && inputValue !== placeholder) {
          const lowerCasedInputValue = inputValue
            ? inputValue.toLowerCase()
            : "";
          const updatedDisplayItems = items.filter((item) => {
            if (
              !inputValue ||
              hashFunction(item)?.toLowerCase()?.includes(lowerCasedInputValue)
            ) {
              return true;
            } else {
              return false;
            }
          });
          setDisplayItems(updatedDisplayItems);
        } else if (isOpen && inputValue === "") {
          setDisplayItems(items);
        }
      },
      onIsOpenChange({ isOpen, selectedItem }) {
        if (isOpen) {
          setInputValue("");
        } else {
          if (selectedItem) {
            setInputValue(hashFunction(selectedItem));
          } else {
            setInputValue("");
          }
        }
      },
      onSelectedItemChange({ selectedItem }) {
        if (selectedItem) {
          onChangeEvent({ id: selectedItem.id });
        } else if (!selectedItem) {
          onChangeEvent({ id: undefined });
          setDisplayItems(items);
        }
      },
      items: displayItems,
      itemToString(item) {
        return item ? hashFunction(item) : "";
      },
    });

    if (selectedValue && !selectedItem) {
      selectItem(selectedValue);
    }

    const handleClearButtonClick = () => {
      selectItem(null);
      onChangeEvent({ id: undefined });
      setDisplayItems(items);
    };
    let clearButton = undefined;
    if (selectedItem) {
      clearButton = (
        <ClearButton onClick={() => handleClearButtonClick()}>
          {ClearButtonSymbol}
        </ClearButton>
      );
    } else {
      clearButton = undefined;
    }

    return (
      <ComboBoxContainer>
        <SrOnlyLabel {...getLabelProps()}>{placeholder}</SrOnlyLabel>
        <DropdownContainer>
          <DropdownInputField
            className={inputValue === "" ? "input--placeholder" : undefined}
            placeholder={placeholder}
            {...getInputProps()}
          />
          <ToggleButton
            aria-label="toggle menu"
            type="button"
            {...getToggleButtonProps()}
          >
            {isOpen ? <ArrowUp /> : <ArrowDown />}
          </ToggleButton>
          {clearButton}
        </DropdownContainer>
        <MainMenu
          {...getMenuProps()}
          style={{ display: isOpen ? "block" : "none" }}
        >
          {isOpen &&
            displayItems.map((item, index) => {
              return (
                <ItemRenderComponent
                  item={item}
                  index={index}
                  highlightedIndex={highlightedIndex}
                  selectedItem={selectedItem}
                  mappedItemIndex={index}
                  getItemProps={getItemProps}
                  hashFunction={hashFunction}
                  displayItems={displayItems}
                  setDisplayItems={setDisplayItems}
                />
              );
            })}
        </MainMenu>
      </ComboBoxContainer>
    );
  }
  return <ComboBox />;
};

export default SearchBoxPrimitive;
